import { CurrencyType } from '@myn/shared/src/constants';

export enum RecommendationStatus {
  ERROR = 'ERROR',
  EXPIRED = 'EXPIRED',
  ACTIVE = 'ACTIVE',
  REJECTED = 'REJECTED',
  ACCEPTED = 'ACCEPTED',
  ACCEPTING = 'ACCEPTING',
  REWORKING = 'REWORKING',
}

export enum PlatformStatus {
  Error = 'error',
  Success = 'success',
  Pending = 'pending',
}

type TaxonomyBudgetType = Partial<{
  currentBudget: number;
  proposedBudget: number;
  currentGoalValues: number;
  proposedGoalValues: number;
  currentCostMargined: number;
  currenBudgetUnmargined: number;
  proposedBudgetUnmargined: number;
  proposedPlatformBudget: number;
  currentPlatformBudget: number;
  mediaPlatformValue: number;
  budgetType: 'daily' | 'lifetime';
}>;

type TaxonomyMulticurrencyBudgetType = {
  currency: string;
  currentBudget: number;
  currentBudgetUnmargined: number;
  currentCostMargined: number;
  proposedBudget: number;
  proposedBudgetUnmargined: number;
};

export type TaxonomyChildrenType = {
  id: string;
  name: string;
  type: string;
  budgetData: TaxonomyBudgetType;
  multicurrencyBudgetData?: TaxonomyMulticurrencyBudgetType;
  children: TaxonomyChildrenType[];
};

export type TaxonomyChildrenTypeWithStatus = Omit<
  TaxonomyChildrenType,
  'children'
> & {
  status: PlatformStatus;
  parent: TaxonomyChildrenType | null;
  children: TaxonomyChildrenTypeWithStatus[];
};

export type ForecastedKPIItem = {
  label: string;
  currentValue: number;
  proposedValue: number;
  histValue: number;
  proposedValueTomorrow: number;
  predictedValueTomorrow: number;
  absoluteChange: number;
  predictedValue: number;
  percentageChange: number | null;
  isPrimary: boolean;
  metricType: string;
  isIncreasePositive: boolean | null;
};

type ForecastedKPIGraphItem = {
  dates: string[];
  label: string;
  isPrimary: boolean;
  currentValues: number[];
  proposedValues: number[];
};

type ForecastedKPIGraph = {
  daily: ForecastedKPIGraphItem[];
  weekly: ForecastedKPIGraphItem[];
};

type ForecastedKPI = {
  graph?: ForecastedKPIGraph;
  widget: ForecastedKPIItem[];
  mainGoal: {
    primaryMetric: string;
    reverseMetric: string;
  };
};

type TaxonomyType = {
  id?: string;
  name: string;
  type: string;
  budgetData: TaxonomyBudgetType;
  children: TaxonomyChildrenType[];
  mediaPlanId?: string;
  target?: string;
  forecastedKpi: ForecastedKPI;
  peripheralName?: string;
  typeOfRecommendation?: string;
};

export type PlatformStatuses = {
  id: number;
  recommendationId: number;
  platform: string;
  data: PlatformStatusesData[];
};

export type PlatformStatusesData = {
  objectId: string;
  objectType: string;
  status: PlatformStatus;
  details: string;
};

export type RecommendationType = {
  id: number;
  uuid: string;
  creationDate: string;
  type: string;
  enabled: boolean;
  mediaPlanId: number;
  accountId: number;
  journeyId: number;
  journeyName: string;
  version: number;
  taxonomy: TaxonomyType;
  userId?: number;
  currency: CurrencyType;
  status: RecommendationStatus;
  decisionTime?: string;
  reason?: string;
  description?: string;
  goal?: string;
  platformStatuses: PlatformStatuses[];
};

export type RecommendationTypeWithStatuses = Omit<
  RecommendationType,
  'taxonomy'
> & {
  taxonomy: Omit<TaxonomyType, 'children'> & {
    children: TaxonomyChildrenTypeWithStatus[];
  };
};

export type RecommendationResponse = {
  page: number;
  pages: number;
  items: RecommendationType[];
};

export type PublishRecommendationState = { id: number; hasError: boolean };

export type RecommendationsState = {
  isLoading: boolean;
  hasNew: boolean | null;
  error: string;
  publishSuggestionStatuses: PublishRecommendationState[];
} & RecommendationResponse;

export type RowType = {
  id: number;
  name: string;
  type: string;
};

export type PlatformType = {
  id: number;
  name: string;
  type: string;
  mediaRows: RowType[];
};
export type Channel = {
  id: number;
  name: string;
  type: string;
  platforms: PlatformType[];
};
export type ChannelChildren = {
  id: string;
  name: string;
  type: string;
  isExist: boolean;
  children: ChannelChildren[];
};

export type RecommendationsSettings = {
  objectPrioritisationGoal: string | null;
  objectPrioritisationOptimization: string | null;
  budgetReallocationLimits: null | {
    min: number;
    max: number;
  };
  mediaOverspendingEnabled: boolean;
  mediaOverspendingLimit: string | null;
  mediaUnderspendingEnabled: boolean;
  mediaUnderspendingLimit: string | null;
  otherAllowSuggestNewPlatforms: boolean;
  otherAllowSuggestStopPlatforms: boolean;
  nonApplicableList: { channels: Channel[] };
};

export type RecommendationsSettingsState = RecommendationsSettings & {
  isLoading: boolean;
  error: string;
};

export type DataWithExistFlag = Channel & {
  id: number;
  name: string;
  type: string;
  platforms: PlatformWithExistFlag[];
  isExist?: boolean;
};

export type PlatformWithExistFlag = PlatformType & {
  id: number;
  name: string;
  type: string;
  mediaRows: RowWithExistFlag[];
  isExist?: boolean;
};

export type RowWithExistFlag = RowType & {
  id: number;
  name: string;
  type: string;
  isExist?: boolean;
};

export type RecoSettingsGoals = {
  expected: string;
  actual: string;
};

export type RecoSettingsPrioritisationGoal = {
  isLoading: boolean;
  error: string;
  prioritisationGoal: null | RecoSettingsGoals;
};

export type ScenarioMultiCurrencyData = {
  currency: string;
  currentPacing: number;
  dailyBudget: number;
  deltaPacing: number;
  pacingCorrection: number;
  plannedBudget: number;
  spent: number;
  suggestedBudget: number;
  suggestedPacing: number;
};

export enum RecommendationItemType {
  Budget = 'budget',
  MediaPlan = 'media_plan',
  Channel = 'channel',
  Platform = 'platform',
  MediaRow = 'media_row',
  PricingItem = 'pricing_item',
}
